// Shared vocabulary for Settings > Integrations, Alert routing and AI.
import { parseTs } from '../SecurityAlerts/model';

// Event types the Worker routes (lib/notify.ts ROUTABLE_EVENTS), in display order.
export const EVENTS = [
  ['ioc', 'IoC alerts', 'A connection or lookup matched threat intel'],
  ['finding', 'Detection findings', 'A detection rule fired'],
  ['cve', 'Vulnerabilities', 'A package has a known CVE'],
  ['agent_silent', 'Agent not reporting', 'An agent has been silent for 24 h'],
  ['workflow', 'Playbook waiting for approval', 'A playbook run needs a person'],
  ['investigation', 'AI investigation summaries', 'An automatic investigation finished'],
];
export const EVENT_LABEL = Object.fromEntries(EVENTS.map(([k, t]) => [k, t]));
export const SEVERITY_CHOICES = [['medium', 'Medium', 'med'], ['high', 'High', 'high'], ['critical', 'Critical', 'crit']];
export const DEDUPE_CHOICES = [[5, '5 minutes'], [15, '15 minutes'], [60, '1 hour']];
export const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

export const LOG_STATUS = {
  sent: ['Sent', 'var(--sev-good-fg)'],
  failed: ['Failed', 'var(--sev-crit-fg)'],
  suppressed: ['Repeat, held back', 'var(--ink-faint)'],
  held: ['Held for quiet hours', 'var(--sev-med-fg)'],
  digested: ['Sent in the morning digest', 'var(--sev-good-fg)'],
};

export function ago(iso) {
  const d = parseTs(iso);
  if (!d) return '';
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} h ago`;
  return `${Math.floor(m / 1440)} d ago`;
}
export function clock(iso) {
  const d = parseTs(iso);
  if (!d) return '';
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return d.toDateString() === new Date().toDateString() ? hm : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${hm}`;
}

// The roadmap: integrations not built yet. next = what we'd build first.
export const ROADMAP_CATS = { chat: 'Alerts & chat', intel: 'Threat intel', resp: 'Response', signin: 'Sign-in', ticket: 'Ticketing', logs: 'Logs & SIEM', cloud: 'Cloud & storage' };
export const ROADMAP = [
  ['chat', 'EM', 'Email', 'SMTP or Microsoft 365. Alerts, member invitations and the weekly summary.', 1, 'Sends alerts'],
  ['chat', 'MT', 'Microsoft Teams', 'Alerts in a Teams channel, with a link straight to the case.', 1, 'Sends alerts'],
  ['chat', 'WH', 'Outgoing webhook', 'Signed JSON to any URL, for tools without a direct integration.', 1, 'Sends alerts'],
  ['chat', 'SL', 'Slack', 'The same messages as Telegram, in a Slack channel.', 0, 'Sends alerts'],
  ['chat', 'WA', 'WhatsApp Business', 'Critical alerts to the on-call phone.', 0, 'Sends alerts'],
  ['chat', 'SM', 'SMS', 'A fallback for critical alerts when chat apps are down.', 0, 'Sends alerts'],
  ['chat', 'PD', 'PagerDuty', 'On-call rotas and escalation when nobody answers.', 0, 'Sends alerts'],
  ['chat', 'OG', 'Opsgenie', 'The same, for teams already on Atlassian.', 0, 'Sends alerts'],
  ['chat', 'DC', 'Discord', 'For student SOC teams and labs that already use it.', 0, 'Sends alerts'],
  ['intel', 'KV', 'CISA KEV', 'Marks CVEs that are being exploited right now. Free, no key.', 1, 'Enriches'],
  ['intel', 'AB', 'AbuseIPDB', 'A reputation score for every outbound IP and IoC alert.', 1, 'Enriches'],
  ['intel', 'VT', 'VirusTotal', 'Hash, domain and IP lookups in findings and the assistant.', 1, 'Enriches'],
  ['intel', 'TF', 'ThreatFox & URLhaus', 'Free malware indicator and URL feeds from abuse.ch, next to MISP.', 0, 'Imports'],
  ['intel', 'OT', 'AlienVault OTX', 'Community threat pulses as another indicator source.', 0, 'Imports'],
  ['intel', 'GN', 'GreyNoise', 'Tells background internet scanning apart from targeted attacks.', 0, 'Enriches'],
  ['intel', 'SH', 'Shodan', 'What your public IP addresses expose to the internet.', 0, 'Enriches'],
  ['intel', 'MC', 'MyCERT advisories', 'Malaysia’s national CERT advisories.', 0, 'Imports'],
  ['resp', 'PF', 'pfSense / OPNsense', 'Block an IP at the edge firewall, not only on one host.', 1, 'Takes action'],
  ['resp', 'FG', 'FortiGate', 'The same block action for Fortinet firewalls.', 0, 'Takes action'],
  ['resp', 'MK', 'MikroTik', 'Block lists on MikroTik routers, common on campus networks.', 0, 'Takes action'],
  ['resp', 'DN', 'DNS sinkhole', 'Send a bad domain to a sinkhole through Pi-hole or AdGuard Home.', 0, 'Takes action'],
  ['signin', 'EN', 'Microsoft Entra ID', 'Staff sign in with their university account. Roles follow groups.', 1, 'Sign-in'],
  ['signin', 'GW', 'Google Workspace', 'The same, for Google accounts.', 0, 'Sign-in'],
  ['signin', 'OI', 'OIDC or SAML', 'Any other identity provider.', 0, 'Sign-in'],
  ['ticket', 'JI', 'Jira', 'Open a ticket from a case and keep the status in step.', 0, 'Takes action'],
  ['ticket', 'SN', 'ServiceNow', 'Incidents for teams that run IT service management on it.', 0, 'Takes action'],
  ['ticket', 'GH', 'GitHub Issues', 'For fixes that belong to a repository, like a vulnerable package.', 0, 'Takes action'],
  ['logs', 'SY', 'Syslog', 'Firewall and switch logs, not only agent snapshots.', 0, 'Imports'],
  ['logs', 'IW', 'Inbound webhook', 'Let other tools post alerts into DragonEye.', 0, 'Imports'],
  ['logs', 'WZ', 'Wazuh', 'Pull Wazuh alerts in, or push findings to it.', 0, 'Imports'],
  ['logs', 'EL', 'Elastic / OpenSearch', 'Forward findings and alerts for long-term search.', 0, 'Exports'],
  ['logs', 'SP', 'Splunk', 'Forward findings and alerts over HTTP Event Collector.', 0, 'Exports'],
  ['cloud', 'GD', 'AWS GuardDuty', 'Findings from AWS accounts next to on-site ones.', 0, 'Imports'],
  ['cloud', 'MD', 'Microsoft Defender', 'Endpoint alerts from Defender for Endpoint.', 0, 'Imports'],
  ['cloud', 'S3', 'S3 or Google Drive', 'Keep exported reports and audit logs off the platform.', 0, 'Exports'],
].map(([cat, mono, name, what, next, kind]) => ({ cat, mono, name, what, next: !!next, kind }));
